import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Checkbox,
  Stack, TextField, Typography, IconButton, Tooltip, MenuItem, ToggleButton,
  ToggleButtonGroup, Grid, Menu, ListItemIcon, ListItemText, Divider,
  LinearProgress, Popover,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Add, ContentCopy, ExpandMore, Lock, Schedule, MoreVert, Visibility,
  Email, Link as LinkIcon, FileDownload, Refresh, Edit, Delete, PlayArrow,
} from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import GeoMasterSelect from '../../components/masters/GeoMasterSelect';
import { usePlatformServices } from '../../hooks/useServiceCatalog';
import { filterTenantServiceModules } from '../../constants/tenantModules';
import { defaultsForCountrySelection, currencyOptionsForCountry, timezoneOptionsForCountry } from '../../constants/countryDefaults';
import { listTenantSubscriptionPlans } from '../../services/tenantSubscriptionPlanService';
import { SUBSCRIPTION_PLANS } from '../../services/tenantService';
import {
  createSalesDemo, updateSalesDemo, extendSalesDemo, listSalesDemos, revokeSalesDemo, deleteSalesDemo,
  resendDemoInvite, DEMO_DURATION_PRESETS,
  type CreateSalesDemoPayload, type CreateSalesDemoResult, type DemoListStatus,
  type ListSalesDemosParams, type SalesDemoRow, type ResendDemoInviteResult,
} from '../../services/salesDemoService';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import PersonNameField from '../../components/ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../../components/ui/forms/PhoneWithDialCodeField';
import { validateEmail, validateLocalPhoneForCountry } from '../../utils/personFieldValidation';
import { composeE164, localPhoneDigits, splitPhoneByDialCodes } from '../../utils/phoneDialCode';
import {
  listCountryDialCodes,
  phoneLengthForDialCode,
  type CountryDialOption,
} from '../../services/countryDialCodeService';

const DEFAULT_MODULES = ['transportation', 'food', 'grocery', 'porter', 'on_demand', 'wallet'];

type DurationPreset = '1' | '3' | '7' | '15' | '30' | 'custom';
type DatePreset = 'all' | '7' | '30' | 'custom';
type DateField = 'createdAt' | 'demoStartsAt' | 'demoExpiresAt';

type CreateFormState = {
  companyName: string;
  contactPerson: string;
  clientEmail: string;
  phone: string;
  phoneCountryCode: string;
  durationPreset: DurationPreset;
  days: number;
  startsAt: string;
  expiresAt: string;
  enabledModules: string[];
  country: string;
  currency: string;
  timezone: string;
  subscriptionPlan: string;
  salesOwner: string;
  notes: string;
  primaryColor: string;
  logo: string;
};

const EMPTY_FORM: CreateFormState = {
  companyName: '',
  contactPerson: '',
  clientEmail: '',
  phone: '',
  phoneCountryCode: '+91',
  durationPreset: '7',
  days: 7,
  startsAt: '',
  expiresAt: '',
  enabledModules: [...DEFAULT_MODULES],
  country: 'IN',
  currency: 'INR',
  timezone: 'Asia/Kolkata',
  subscriptionPlan: 'starter',
  salesOwner: '',
  notes: '',
  primaryColor: '#173A5E',
  logo: '',
};

type DemoFieldKey = 'companyName' | 'clientEmail' | 'phone';

function extractError(err: unknown) {
  const e = err as { response?: { data?: { message?: string; error?: string; errors?: unknown[] } } };
  const data = e.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    const parts = data.errors
      .map((item) => {
        const row = item as { message?: string; msg?: string };
        return row.message || row.msg;
      })
      .filter(Boolean) as string[];
    if (parts.length) return parts.join('. ');
  }
  return data?.message || data?.error || 'Something went wrong';
}

function extractDemoFieldErrors(err: unknown): Partial<Record<DemoFieldKey, string>> {
  const response = (err as { response?: { status?: number; data?: { errors?: unknown; message?: string; error?: string } } })
    ?.response;
  const raw = response?.data;
  const out: Partial<Record<DemoFieldKey, string>> = {};
  const list = raw?.errors;
  if (Array.isArray(list)) {
    for (const item of list) {
      const row = item as { field?: string; message?: string; path?: string; param?: string; msg?: string };
      const field = String(row.field || row.path || row.param || '');
      const message = row.message || row.msg;
      if (!message) continue;
      if (field === 'companyName' || field === 'clientEmail' || field === 'phone') {
        out[field] = message;
      }
    }
  }
  const message = `${raw?.message || raw?.error || ''}`.toLowerCase();
  if (!out.clientEmail && response?.status === 409) {
    out.clientEmail = raw?.message || raw?.error || 'This email is already used for another demo client';
  }
  if (!out.companyName && message.includes('company name') && message.includes('already')) {
    out.companyName = raw?.message || raw?.error || 'This company name is already used for another demo client';
  }
  if (!out.clientEmail && message.includes('email') && message.includes('already')) {
    out.clientEmail = raw?.message || raw?.error || 'This email is already used for another demo client';
  }
  if (!out.phone && message.includes('phone') && message.includes('already')) {
    out.phone = raw?.message || raw?.error || 'This phone number is already used for another demo client';
  }
  return out;
}

function copyText(value: string) {
  void navigator.clipboard?.writeText(value);
}

function splitDemoPhone(
  rawPhone: string | undefined,
  dialOptions: CountryDialOption[],
  preferredCountry = 'IN',
): { phone: string; phoneCountryCode: string } {
  const dialCodes = dialOptions.map((o) => o.dialCode);
  const split = splitPhoneByDialCodes(rawPhone || '', dialCodes);
  const preferred = dialOptions.find((o) => o.code === preferredCountry.toUpperCase());
  return {
    phone: split.localNumber || localPhoneDigits(rawPhone),
    phoneCountryCode: split.dialCode || preferred?.dialCode || '',
  };
}

/** Calendar YYYY-MM-DD from an ISO instant using UTC parts (avoids IST off-by-one on end-of-day UTC). */
function toDateOnlyString(value?: string | Date | null): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Today's calendar date in UTC (for date inputs). */
function todayDateOnly(): string {
  return toDateOnlyString(new Date());
}

function toDateInputValue(d: Date | string) {
  return toDateOnlyString(d);
}

/** Add whole calendar days to a YYYY-MM-DD string (UTC noon — no DST skew). */
function addCalendarDays(ymd: string, days: number): string {
  const base = toDateOnlyString(ymd);
  if (!base) return '';
  const d = new Date(`${base}T12:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateOnlyString(d);
}

function addDays(base: Date, days: number) {
  return new Date(base.getTime() + days * 86400000);
}

/** Inclusive calendar-day span; 0 when missing/invalid or end is on/before start. */
function daysBetween(start: string, end: string) {
  const sYmd = toDateOnlyString(start);
  const eYmd = toDateOnlyString(end);
  if (!sYmd || !eYmd) return 0;
  const s = new Date(`${sYmd}T00:00:00.000Z`).getTime();
  const e = new Date(`${eYmd}T00:00:00.000Z`).getTime();
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return 0;
  return Math.round((e - s) / 86400000);
}

function isStartAfterEnd(start?: string, end?: string) {
  const sYmd = toDateOnlyString(start);
  const eYmd = toDateOnlyString(end);
  if (!sYmd || !eYmd) return false;
  return sYmd > eYmd;
}

function demoWindowError(start?: string, end?: string): string {
  if (!start?.trim() || !end?.trim()) return '';
  if (isStartAfterEnd(start, end)) {
    return 'Start date must not be after the end date';
  }
  const days = daysBetween(start, end);
  if (days < 1) return 'End date must be after start date';
  if (days > 90) return 'Demo window cannot exceed 90 days';
  return '';
}

/** Display configured demo period as DD/MM/YYYY (UTC calendar day). */
function formatDemoDate(value?: string | null) {
  const ymd = toDateOnlyString(value);
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function computeRemainingDays(expiresAt?: string | null, fallback?: number) {
  if (expiresAt) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (Number.isNaN(ms)) return Math.max(0, fallback ?? 0);
    if (ms <= 0) return 0;
    return Math.max(0, Math.ceil(ms / 86400000));
  }
  return Math.max(0, fallback ?? 0);
}

/** Expired / locked / revoked — date is the source of truth when past end. */
function isDemoRowExpired(row: Pick<
  SalesDemoRow,
  'lifecycleStatus' | 'expired' | 'status' | 'demoLockedAt' | 'demoExpiresAt' | 'remainingDays'
>) {
  if (row.demoExpiresAt) {
    const ms = new Date(row.demoExpiresAt).getTime() - Date.now();
    if (!Number.isNaN(ms) && ms <= 0) return true;
  }
  if (row.lifecycleStatus === 'expired' || row.expired) return true;
  if (row.status === 'suspended' || row.status === 'deactivated' || row.status === 'deleted') return true;
  if (row.demoLockedAt) return true;
  return false;
}

function resolveLifecycleStatus(row: SalesDemoRow): 'scheduled' | 'active' | 'expired' {
  // Expiry date wins over a stale Active DB status.
  if (isDemoRowExpired(row)) return 'expired';
  if (row.lifecycleStatus === 'scheduled' || row.notYetStarted) {
    if (row.demoStartsAt && new Date(row.demoStartsAt).getTime() > Date.now()) return 'scheduled';
  }
  if (row.demoStartsAt && new Date(row.demoStartsAt).getTime() > Date.now()) return 'scheduled';
  return 'active';
}

function remainingDaysForRow(row: SalesDemoRow) {
  if (resolveLifecycleStatus(row) === 'expired') return 0;
  return computeRemainingDays(row.demoExpiresAt, row.remainingDays);
}

function demoLoginUrl(slug?: string, portal: 'admin' | 'vendor' = 'admin') {
  const params = new URLSearchParams();
  if (slug) params.set('tenant', slug);
  if (portal === 'vendor') params.set('portal', 'vendor');
  const q = params.toString();
  return `${window.location.origin}/login${q ? `?${q}` : ''}`;
}

function enabledServiceKeys(row: SalesDemoRow) {
  return (row.modules || []).filter((m) => m.enabled).map((m) => m.key);
}

function serviceLabel(code: string, nameByCode?: Map<string, string>) {
  return nameByCode?.get(code) || code.replace(/_/g, ' ');
}

/** Shows first N services; +count opens remaining on hover/click. */
function ServicesCell({
  keys,
  nameByCode,
  visible = 4,
}: {
  keys: string[];
  nameByCode?: Map<string, string>;
  visible?: number;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!keys.length) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const shown = keys.slice(0, visible);
  const hidden = keys.slice(visible);
  const hiddenLabels = hidden.map((k) => serviceLabel(k, nameByCode));

  return (
    <>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ py: 0.5, overflow: 'hidden', alignItems: 'center' }}>
        {shown.map((k) => (
          <Chip key={k} size="small" label={serviceLabel(k, nameByCode)} sx={{ height: 22, fontSize: 11 }} />
        ))}
        {hidden.length > 0 && (
          <Tooltip
            title={(
              <Stack spacing={0.5} sx={{ py: 0.25 }}>
                <Typography variant="caption" fontWeight={700}>
                  +{hidden.length} more service{hidden.length === 1 ? '' : 's'}
                </Typography>
                {hiddenLabels.map((label) => (
                  <Typography key={label} variant="caption">• {label}</Typography>
                ))}
              </Stack>
            )}
            arrow
            placement="top"
          >
            <Chip
              size="small"
              clickable
              color="primary"
              variant="outlined"
              label={`+${hidden.length}`}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(e.currentTarget);
              }}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
              aria-label={`Show ${hidden.length} more services`}
            />
          </Tooltip>
        )}
      </Stack>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack spacing={1} sx={{ p: 1.5, maxWidth: 280 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Additional services ({hidden.length})
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {hidden.map((k) => (
              <Chip key={k} size="small" label={serviceLabel(k, nameByCode)} />
            ))}
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

function resolveListDateRange(preset: DatePreset, customFrom: string, customTo: string) {
  if (preset === 'all') return { from: undefined as string | undefined, to: undefined as string | undefined };
  if (preset === 'custom') {
    if (customFrom && customTo && isStartAfterEnd(customFrom, customTo)) {
      return { from: undefined, to: undefined };
    }
    return {
      from: customFrom || undefined,
      to: customTo || undefined,
    };
  }
  const days = preset === '7' ? 7 : 30;
  const to = new Date();
  const from = addDays(to, -days);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

/** Active from/to values used for API filters (presets fill the calendar fields). */
function activeFilterRange(preset: DatePreset, customFrom: string, customTo: string) {
  if (preset === 'all') return { from: undefined as string | undefined, to: undefined as string | undefined };
  if (preset === '7' || preset === '30') return resolveListDateRange(preset, '', '');
  if (customFrom && customTo && isStartAfterEnd(customFrom, customTo)) {
    return { from: undefined, to: undefined };
  }
  return {
    from: customFrom || undefined,
    to: customTo || undefined,
  };
}

function buildCreatePayload(form: CreateFormState): CreateSalesDemoPayload {
  const contact = form.contactPerson.trim();
  const payload: CreateSalesDemoPayload = {
    companyName: form.companyName.trim(),
    clientEmail: form.clientEmail.trim(),
    contactPerson: contact || undefined,
    clientName: contact || undefined,
    phone: composeE164(form.phoneCountryCode, form.phone) || undefined,
    enabledModules: form.enabledModules,
    country: form.country || undefined,
    currency: form.currency || undefined,
    timezone: form.timezone || undefined,
    subscriptionPlan: form.subscriptionPlan || undefined,
    salesOwner: form.salesOwner.trim() || undefined,
    notes: form.notes.trim() || undefined,
  };

  if (form.primaryColor || form.logo.trim()) {
    payload.branding = {
      primaryColor: form.primaryColor || undefined,
      logo: form.logo.trim() || undefined,
    };
  }

  if (form.durationPreset === 'custom' && form.startsAt && form.expiresAt) {
    if (demoWindowError(form.startsAt, form.expiresAt)) {
      // Caller must block submit; keep payload free of inverted dates.
    } else {
      payload.startsAt = form.startsAt;
      payload.expiresAt = form.expiresAt;
    }
  } else if (form.startsAt && form.expiresAt && !demoWindowError(form.startsAt, form.expiresAt)) {
    payload.startsAt = form.startsAt;
    payload.expiresAt = form.expiresAt;
  } else if (form.startsAt) {
    payload.startsAt = form.startsAt;
    payload.days = form.days;
  } else {
    payload.days = form.days;
  }

  return payload;
}

function formFromDemoRow(row: SalesDemoRow, dialOptions: CountryDialOption[] = []): CreateFormState {
  const startsAt = toDateOnlyString(row.demoStartsAt);
  const expiresAt = toDateOnlyString(row.demoExpiresAt);
  const windowDays = startsAt && expiresAt ? daysBetween(startsAt, expiresAt) : 0;
  const invalidWindow = Boolean(startsAt && expiresAt && windowDays < 1);
  const days = invalidWindow
    ? 7
    : windowDays
      || computeRemainingDays(row.demoExpiresAt, row.remainingDays)
      || 7;
  const presetMatch = !invalidWindow ? DEMO_DURATION_PRESETS.find((p) => p === days) : undefined;
  const modules = enabledServiceKeys(row);
  const phoneSplit = splitDemoPhone(row.phone, dialOptions, row.country || 'IN');
  return {
    companyName: row.companyName || '',
    contactPerson: row.contactPerson || '',
    clientEmail: row.demoClientEmail || '',
    phone: phoneSplit.phone,
    phoneCountryCode: phoneSplit.phoneCountryCode,
    durationPreset: presetMatch ? (String(presetMatch) as DurationPreset) : 'custom',
    days: Math.max(1, Math.min(90, days)),
    startsAt,
    expiresAt,
    enabledModules: modules.length ? modules : [...DEFAULT_MODULES],
    country: row.country || 'IN',
    currency: row.currency || 'INR',
    timezone: row.timezone || 'Asia/Kolkata',
    subscriptionPlan: row.subscriptionPlan || 'starter',
    salesOwner: row.demoSalesOwner || '',
    notes: row.demoNotes || '',
    primaryColor: row.branding?.primaryColor || '#173A5E',
    logo: row.branding?.logo || '',
  };
}

function seedStatusOf(row: SalesDemoRow) {
  return row.seedStatus || row.demoSeedStatus;
}

function seedPercentOf(row: SalesDemoRow) {
  if (typeof row.seedPercent === 'number') return row.seedPercent;
  if (typeof row.demoSeedPercent === 'number') return row.demoSeedPercent;
  const status = seedStatusOf(row);
  if (status === 'completed') return 100;
  if (status === 'pending') return 0;
  return 0;
}

function seedProgressCell(row: SalesDemoRow) {
  const status = seedStatusOf(row);
  const percent = seedPercentOf(row);
  if (status === 'completed') {
    return <Chip size="small" label="Complete" color="success" />;
  }
  if (status === 'failed') {
    const hint = row.demoSeedErrors?.length ? row.demoSeedErrors[0] : 'Sandbox seed failed';
    return (
      <Tooltip title={hint}>
        <Chip size="small" label={`Failed ${percent}%`} color="error" />
      </Tooltip>
    );
  }
  if (status === 'running' || status === 'pending') {
    const label = status === 'pending' ? 'Queued' : `${percent}%`;
    return (
      <Box sx={{ width: '100%', pr: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <LinearProgress
          variant={status === 'pending' && percent === 0 ? 'indeterminate' : 'determinate'}
          value={percent}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    );
  }
  return <Typography variant="caption" color="text.secondary">—</Typography>;
}

function statusChip(row: SalesDemoRow) {
  const life = resolveLifecycleStatus(row);
  if (life === 'scheduled') {
    return <Chip size="small" label="Scheduled" color="info" />;
  }
  if (life === 'expired') {
    return <Chip size="small" label="Expired" color="warning" />;
  }
  return <Chip size="small" label="Active" color="success" />;
}

function statusLabel(row: SalesDemoRow) {
  const life = resolveLifecycleStatus(row);
  if (life === 'scheduled') return 'Scheduled';
  if (life === 'expired') return 'Expired';
  return 'Active';
}

function exportDemosCsv(rows: SalesDemoRow[]) {
  const headers = [
    'Company', 'Email', 'Slug', 'Start', 'End', 'Remaining Days', 'Status', 'Services', 'Country', 'Currency', 'Owner',
  ];
  const lines = rows.map((row) => {
    const start = row.demoStartsAt ? formatDemoDate(row.demoStartsAt) : '';
    const end = row.demoExpiresAt ? formatDemoDate(row.demoExpiresAt) : '';
    const remaining = remainingDaysForRow(row);
    const services = enabledServiceKeys(row).join('; ');
    const cells = [
      row.companyName,
      row.demoClientEmail || '',
      row.slug,
      start,
      end,
      String(remaining),
      statusLabel(row),
      services,
      row.country || '',
      row.currency || '',
      row.demoSalesOwner || '',
    ];
    return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
  });
  const csv = [headers.map((h) => `"${h}"`).join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `demo-clients-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function CredentialField({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <TextField fullWidth label={label} value={value} InputProps={{ readOnly: true }} />
      <IconButton onClick={() => copyText(value)} aria-label={`Copy ${label}`}>
        <ContentCopy />
      </IconButton>
    </Stack>
  );
}

const DEFAULT_APP_LINKS = {
  user: 'https://play.google.com/store/apps/details?id=com.supersaas.super_saas_mobile',
  vendor: 'https://play.google.com/store/apps/details?id=com.supersaas.vendor_app',
  partner: 'https://play.google.com/store/apps/details?id=com.supersaas.partner_app',
  provider: 'https://play.google.com/store/apps/details?id=com.supersaas.provider_app',
};

const DEFAULT_APPLE_LINKS = {
  user: 'https://apps.apple.com/in/app/taxiapps-proplus-user/id6800609891',
  vendor: 'https://apps.apple.com/in/app/taxiappz-proplus-vendor/id6801101661',
  partner: 'https://apps.apple.com/in/app/taxiapps-proplus-partner/id6801026856',
  provider: 'https://apps.apple.com/in/app/taxiapps-proplus-provider/id6801138889',
};

function appleShareLines(apple: typeof DEFAULT_APPLE_LINKS): string[] {
  return [
    'Mobile apps (App Store)',
    `User app: ${apple.user}`,
    `Delivery Partner app: ${apple.partner}`,
    `On-demand Provider app: ${apple.provider}`,
    `Vendor app: ${apple.vendor}`,
    '',
  ];
}

function buildDemoInviteShareText(created: CreateSalesDemoResult): string {
  const apps = created.appLinks || DEFAULT_APP_LINKS;
  const apple = created.appleLinks || DEFAULT_APPLE_LINKS;
  const adminUrl = created.demoUrl || demoLoginUrl(created.tenant.slug);
  const vendorUrl = created.vendorUrl || demoLoginUrl(created.tenant.slug, 'vendor');
  return [
    `Your xmoveon demo for "${created.tenant.companyName}" is ready.`,
    '',
    `Demo key: ${created.demoKey}`,
    `Valid upto: ${formatDemoDate(String(created.expiresAt))}`,
    '',
    'Admin panel',
    `Email / username: ${created.adminEmail || created.tenant.demoClientEmail || ''}`,
    `Password: ${created.adminPassword}`,
    `Admin URL: ${adminUrl}`,
    '',
    'Vendor panel',
    `Email / username: ${created.vendorEmail || ''}`,
    `Password: ${created.vendorPassword || ''}`,
    `Vendor URL: ${vendorUrl}`,
    '',
    'Mobile apps (Google Play)',
    `User app: ${apps.user}`,
    `Delivery Partner app: ${apps.partner}`,
    `On-demand Provider app: ${apps.provider}`,
    `Vendor app: ${apps.vendor}`,
    '',
    ...appleShareLines(apple),
    '— xmoveon',
  ].join('\n');
}

function buildResendInviteShareText(invite: ResendDemoInviteResult): string {
  const apps = invite.appLinks || DEFAULT_APP_LINKS;
  const apple = invite.appleLinks || DEFAULT_APPLE_LINKS;
  const adminUrl = invite.demoUrl || demoLoginUrl(invite.slug);
  const vendorUrl = invite.vendorUrl || demoLoginUrl(invite.slug, 'vendor');
  const company = invite.companyName || 'your company';
  const lines = [
    `Your xmoveon demo login for "${company}" has been updated.`,
    '',
    `Demo key: ${invite.demoKey}`,
  ];
  if (invite.expiresAt) {
    lines.push(`Valid upto: ${formatDemoDate(String(invite.expiresAt))}`);
  }
  lines.push(
    '',
    'Admin panel',
    `Email / username: ${invite.adminEmail || ''}`,
    `Password: ${invite.adminPassword}`,
    `Admin URL: ${adminUrl}`,
    '',
    'Vendor panel',
    `Email / username: ${invite.vendorEmail || ''}`,
    `Password: ${invite.vendorPassword || ''}`,
    `Vendor URL: ${vendorUrl}`,
    '',
    'Mobile apps (Google Play)',
    `User app: ${apps.user}`,
    `Delivery Partner app: ${apps.partner}`,
    `On-demand Provider app: ${apps.provider}`,
    `Vendor app: ${apps.vendor}`,
    '',
    ...appleShareLines(apple),
    '— xmoveon',
  );
  return lines.join('\n');
}

export default function SalesDemosPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: platformServices = [] } = usePlatformServices();
  const serviceModules = useMemo(() => filterTenantServiceModules(platformServices), [platformServices]);
  const serviceNameByCode = useMemo(
    () => new Map(serviceModules.map((m) => [m.code, m.name || m.code])),
    [serviceModules],
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DemoListStatus>('all');
  const [dateField, setDateField] = useState<DateField>('demoStartsAt');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSlug, setEditingSlug] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [duplicateFieldErrors, setDuplicateFieldErrors] = useState<
    Partial<Record<DemoFieldKey, string>>
  >({});
  const [created, setCreated] = useState<CreateSalesDemoResult | null>(null);
  const [form, setForm] = useState<CreateFormState>({ ...EMPTY_FORM, enabledModules: [...DEFAULT_MODULES] });

  const [viewTarget, setViewTarget] = useState<SalesDemoRow | null>(null);
  const [extendTarget, setExtendTarget] = useState<SalesDemoRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SalesDemoRow | null>(null);
  const [extendDays, setExtendDays] = useState(7);
  const [extendPreset, setExtendPreset] = useState<DurationPreset>('7');
  const [extendExpiresAt, setExtendExpiresAt] = useState('');
  const [extendError, setExtendError] = useState('');

  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const [actionRow, setActionRow] = useState<SalesDemoRow | null>(null);
  const [actionFlash, setActionFlash] = useState<{ severity: 'success' | 'info' | 'warning'; message: string } | null>(null);
  const [inviteResult, setInviteResult] = useState<ResendDemoInviteResult | null>(null);
  const [statusToggleId, setStatusToggleId] = useState<string | null>(null);

  const isEditMode = Boolean(editingId);

  const { data: dialOptions = [] } = useQuery({
    queryKey: ['country-dial-codes'],
    queryFn: listCountryDialCodes,
    staleTime: 5 * 60_000,
  });

  const openCreateDialog = () => {
    setFormError('');
    setShowFormErrors(false);
    setEmailTouched(false);
    setDuplicateFieldErrors({});
    setAdvancedOpen(false);
    setEditingId(null);
    setEditingSlug('');
    const preferred = dialOptions.find((o) => o.code === 'IN')
      || dialOptions.find((o) => o.dialCode === '+91');
    setForm({
      ...EMPTY_FORM,
      enabledModules: [...DEFAULT_MODULES],
      phoneCountryCode: preferred?.dialCode || '+91',
    });
    setCreateOpen(true);
  };

  const openEditDialog = (row: SalesDemoRow) => {
    setFormError('');
    setShowFormErrors(false);
    setEmailTouched(false);
    setDuplicateFieldErrors({});
    setAdvancedOpen(true);
    setEditingId(row._id);
    setEditingSlug(row.slug);
    setForm(formFromDemoRow(row, dialOptions));
    setCreateOpen(true);
  };

  const closeFormDialog = () => {
    setCreateOpen(false);
    setEditingId(null);
    setEditingSlug('');
  };

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      openCreateDialog();
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when ?create=1 is present
  }, [searchParams]);

  const { data: plans = [] } = useQuery({
    queryKey: ['tenant-subscription-plans', 'public'],
    queryFn: () => listTenantSubscriptionPlans({ active: true, public: true }),
    staleTime: 60_000,
  });

  const planOptions = plans.length
    ? plans.map((p) => ({ value: p.code, label: p.name || p.code }))
    : SUBSCRIPTION_PLANS;

  const listRange = useMemo(
    () => activeFilterRange(datePreset, customFrom, customTo),
    [datePreset, customFrom, customTo],
  );

  const dateRangeInvalid = Boolean(customFrom && customTo && isStartAfterEnd(customFrom, customTo));

  const listParams = useMemo(() => {
    // Status + date range are always combined with AND on the server.
    const status = statusFilter === 'all'
      ? undefined
      : statusFilter === 'locked'
        ? 'expired'
        : statusFilter;
    const base: ListSalesDemosParams = {
      page: pagination.page + 1,
      limit: pagination.pageSize,
      search: search || undefined,
      status,
    };
    if (dateRangeInvalid || (!listRange.from && !listRange.to)) return base;
    if (dateField === 'createdAt') {
      return { ...base, createdFrom: listRange.from, createdTo: listRange.to };
    }
    if (dateField === 'demoStartsAt') {
      return { ...base, startsFrom: listRange.from, startsTo: listRange.to };
    }
    return { ...base, expiresFrom: listRange.from, expiresTo: listRange.to };
  }, [pagination.page, pagination.pageSize, search, statusFilter, dateField, listRange, dateRangeInvalid]);

  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    setPagination((p) => ({ ...p, page: 0 }));
    if (preset === 'all') {
      setCustomFrom('');
      setCustomTo('');
      return;
    }
    if (preset === '7' || preset === '30') {
      const range = resolveListDateRange(preset, '', '');
      setCustomFrom(range.from || '');
      setCustomTo(range.to || '');
      return;
    }
    // Custom: keep existing range, or seed last 7 days so From/To calendars are filled
    if (!customFrom && !customTo) {
      const range = resolveListDateRange('7', '', '');
      setCustomFrom(range.from || '');
      setCustomTo(range.to || '');
    }
  };

  const setFilterFrom = (value: Dayjs | null) => {
    setDatePreset('custom');
    setCustomFrom(value?.isValid() ? value.format('YYYY-MM-DD') : '');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const setFilterTo = (value: Dayjs | null) => {
    setDatePreset('custom');
    setCustomTo(value?.isValid() ? value.format('YYYY-MM-DD') : '');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const dateFieldLabel =
    dateField === 'demoStartsAt' ? 'Start date' : dateField === 'demoExpiresAt' ? 'End date' : 'Created at';
  const statusFilterLabel =
    statusFilter === 'all' ? 'All statuses' : statusFilter === 'active' ? 'Active' : 'Expired';

  const queryKey = ['sales-demos', listParams];
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => listSalesDemos(listParams),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.data || [];
      const seeding = rows.some((row) => {
        const status = row.seedStatus || row.demoSeedStatus;
        return status === 'pending' || status === 'running';
      });
      return seeding ? 2500 : false;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sales-demos'] });

  const createMut = useMutation({
    mutationFn: createSalesDemo,
    onSuccess: (result) => {
      invalidate();
      setCreated(result);
      closeFormDialog();
      setFormError('');
      setDuplicateFieldErrors({});
      setAdvancedOpen(false);
      setForm({ ...EMPTY_FORM, enabledModules: [...DEFAULT_MODULES], phoneCountryCode: '+91' });
      setActionFlash({
        severity: result.emailDelivered ? 'success' : 'warning',
        message: result.emailDelivered
          ? `Demo created — invite emailed to ${result.adminEmail || result.tenant.demoClientEmail}`
          : (result.emailReason
            || 'Demo created, but invite email was not delivered. Copy or share details from the Demo ready dialog.'),
      });
    },
    onError: (err) => {
      const fields = extractDemoFieldErrors(err);
      setDuplicateFieldErrors(fields);
      setShowFormErrors(true);
      setEmailTouched(true);
      setFormError(extractError(err));
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateSalesDemoPayload }) =>
      updateSalesDemo(id, payload),
    onSuccess: (row) => {
      invalidate();
      closeFormDialog();
      setFormError('');
      setDuplicateFieldErrors({});
      setAdvancedOpen(false);
      setForm({ ...EMPTY_FORM, enabledModules: [...DEFAULT_MODULES] });
      setActionFlash({
        severity: 'success',
        message: row.expiryUpdated && row.emailDelivered
          ? 'Demo updated — invite emailed with new Valid Upto.'
          : row.expiryUpdated
            ? 'Demo updated (expiry changed; email not delivered — check SMTP).'
            : 'Demo client updated.',
      });
    },
    onError: (err) => {
      const fields = extractDemoFieldErrors(err);
      setDuplicateFieldErrors(fields);
      setShowFormErrors(true);
      setEmailTouched(true);
      setFormError(extractError(err));
    },
  });

  const formSaving = createMut.isPending || updateMut.isPending;

  const extendMut = useMutation({
    mutationFn: ({ id, days, expiresAt }: { id: string; days?: number; expiresAt?: string }) =>
      extendSalesDemo(id, expiresAt ? { expiresAt } : { days: days ?? 7 }),
    onSuccess: (row) => {
      invalidate();
      setExtendTarget(null);
      setExtendError('');
      setStatusToggleId(null);
      setActionFlash({
        severity: 'success',
        message: row?.emailDelivered
          ? 'Demo enabled — invite emailed with new Valid Upto (+7 days).'
          : 'Demo enabled (+7 days).',
      });
    },
    onError: (err) => {
      setStatusToggleId(null);
      setExtendError(extractError(err));
      setActionFlash({ severity: 'warning', message: extractError(err) });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeSalesDemo(id),
    onSuccess: () => {
      invalidate();
      setStatusToggleId(null);
      setActionFlash({ severity: 'warning', message: 'Demo disabled. Access revoked; data is retained.' });
    },
    onError: (err) => {
      setStatusToggleId(null);
      setActionFlash({ severity: 'warning', message: extractError(err) });
    },
  });

  const toggleDemoStatus = (row: SalesDemoRow, next: 'active' | 'expired') => {
    const current = resolveLifecycleStatus(row) === 'expired' ? 'expired' : 'active';
    if (next === current) return;
    setStatusToggleId(row._id);
    if (next === 'expired') {
      revokeMut.mutate(row._id);
      return;
    }
    extendMut.mutate({ id: row._id, days: 7 });
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSalesDemo(id),
    onSuccess: (_data, id) => {
      const name = deleteTarget?.companyName || 'Demo client';
      invalidate();
      setDeleteTarget(null);
      setViewTarget((prev) => (prev?._id === id ? null : prev));
      setActionFlash({
        severity: 'success',
        message: `"${name}" has been deleted. All demo data for this client was permanently removed.`,
      });
    },
    onError: (err) => setActionFlash({ severity: 'warning', message: extractError(err) }),
  });

  const resendMut = useMutation({
    mutationFn: (id: string) => resendDemoInvite(id),
    onSuccess: (result) => {
      invalidate();
      setInviteResult(result);
      setCreated((prev) => (prev && prev.tenant._id === result.tenantId
        ? {
          ...prev,
          demoKey: result.demoKey,
          adminPassword: result.adminPassword,
          adminEmail: result.adminEmail || prev.adminEmail,
          vendorEmail: result.vendorEmail || prev.vendorEmail,
          vendorPassword: result.vendorPassword || prev.vendorPassword,
          demoUrl: result.demoUrl || prev.demoUrl,
          vendorUrl: result.vendorUrl || prev.vendorUrl,
          appLinks: result.appLinks || prev.appLinks,
          appleLinks: result.appleLinks || prev.appleLinks,
          emailDelivered: result.emailDelivered,
          emailMode: result.emailMode,
          emailReason: result.emailReason,
          smtpConfigured: result.smtpConfigured,
        }
        : prev));
      setActionFlash({
        severity: result.emailDelivered ? 'success' : 'warning',
        message: result.emailDelivered
          ? `Login email resent to ${result.adminEmail || 'client'}.`
          : (result.emailReason
            || 'Invite regenerated but email was not delivered. Use Copy full invite / Open in email app.'),
      });
    },
    onError: (err) => setActionFlash({ severity: 'warning', message: extractError(err) }),
  });

  const patchForm = (partial: Partial<CreateFormState>) => {
    setForm((prev) => ({ ...prev, ...partial }));
    if (
      partial.companyName !== undefined
      || partial.clientEmail !== undefined
      || partial.phone !== undefined
      || partial.phoneCountryCode !== undefined
    ) {
      setDuplicateFieldErrors((prev) => {
        const next = { ...prev };
        if (partial.companyName !== undefined) delete next.companyName;
        if (partial.clientEmail !== undefined) delete next.clientEmail;
        if (partial.phone !== undefined || partial.phoneCountryCode !== undefined) delete next.phone;
        return next;
      });
    }
  };

  const applyDurationPreset = (preset: DurationPreset) => {
    if (preset === 'custom') {
      const start = form.startsAt || todayDateOnly();
      let end = form.expiresAt || addCalendarDays(start, form.days || 7);
      if (isStartAfterEnd(start, end)) {
        end = addCalendarDays(start, form.days || 7);
      }
      patchForm({
        durationPreset: 'custom',
        startsAt: start,
        expiresAt: end,
        days: daysBetween(start, end) || form.days || 7,
      });
      setFormError('');
      return;
    }
    const days = Number(preset);
    const start = form.startsAt || todayDateOnly();
    const expiresAt = addCalendarDays(start, days);
    patchForm({
      durationPreset: preset,
      days,
      startsAt: start,
      expiresAt,
    });
    setFormError('');
  };

  const applyExtendPreset = (preset: DurationPreset) => {
    setExtendPreset(preset);
    if (preset === 'custom') {
      if (!extendExpiresAt && extendTarget?.demoExpiresAt) {
        setExtendExpiresAt(addCalendarDays(toDateOnlyString(extendTarget.demoExpiresAt), 7));
      }
      return;
    }
    setExtendDays(Number(preset));
  };

  const toggleModule = (code: string) => {
    setForm((prev) => ({
      ...prev,
      enabledModules: prev.enabledModules.includes(code)
        ? prev.enabledModules.filter((m) => m !== code)
        : [...prev.enabledModules, code],
    }));
  };

  const closeActionMenu = () => {
    setActionAnchor(null);
    setActionRow(null);
  };

  const customWindowDays = form.startsAt && form.expiresAt ? daysBetween(form.startsAt, form.expiresAt) : 0;
  const windowErr = demoWindowError(form.startsAt, form.expiresAt);
  const companyError = !form.companyName.trim() ? 'Company is required' : undefined;
  const emailError = validateEmail(form.clientEmail, 'Email', { required: true });
  const showEmailFieldError = Boolean(
    duplicateFieldErrors.clientEmail
    || (emailError && (emailTouched || showFormErrors || Boolean(form.clientEmail.trim()))),
  );
  const emailHelperText = duplicateFieldErrors.clientEmail
    || (showEmailFieldError && emailError
      ? emailError
      : undefined)
    || (!form.clientEmail.trim()
      ? 'Required * — invite with demo key and login is sent here'
      : (isEditMode ? 'Updates demo client + admin login email' : 'Invite with demo key + login is sent here'));
  const localPhone = localPhoneDigits(form.phone);
  const dialCode = form.phoneCountryCode.trim() || '+91';
  const phoneLengthRule = phoneLengthForDialCode(dialCode);
  let phoneError: string | undefined;
  if (localPhone) {
    if (!form.phoneCountryCode.trim()) {
      phoneError = 'Select a country code before entering the phone number';
    } else {
      phoneError = validateLocalPhoneForCountry(
        localPhone,
        phoneLengthRule,
        'Phone number',
        { dialCode },
      );
    }
  }
  if (!phoneError && duplicateFieldErrors.phone) {
    phoneError = duplicateFieldErrors.phone;
  }
  const phoneLengthHint = phoneLengthRule
    ? (phoneLengthRule.min === phoneLengthRule.max
      ? `Must be exactly ${phoneLengthRule.min} digits for ${dialCode}`
      : `Must be ${phoneLengthRule.min}–${phoneLengthRule.max} digits for ${dialCode}`)
    : `Select a country code, then enter the phone number`;
  const showPhoneFieldError = Boolean(
    phoneError && (showFormErrors || localPhone || duplicateFieldErrors.phone),
  );
  const countryError = !form.country.trim() ? 'Country is required' : undefined;
  const modulesError = form.enabledModules.length < 1 ? 'Select at least one service' : undefined;
  const customDatesError = form.durationPreset === 'custom'
    && (!form.startsAt || !form.expiresAt || customWindowDays < 1 || customWindowDays > 90
      || Boolean(windowErr))
    ? (windowErr || 'Start and end dates are required for a custom duration')
    : undefined;
  const formInvalid = Boolean(
    companyError
    || emailError
    || phoneError
    || countryError
    || modulesError
    || customDatesError
    || windowErr,
  );
  const createDisabled = formSaving || formInvalid;

  const submitForm = () => {
    if (formInvalid) {
      setShowFormErrors(true);
      setEmailTouched(true);
      setFormError(
        companyError
        || emailError
        || phoneError
        || countryError
        || modulesError
        || customDatesError
        || windowErr
        || 'Fill all required fields before saving',
      );
      return;
    }
    setShowFormErrors(false);
    setFormError('');
    const payload = buildCreatePayload(form);
    if (form.durationPreset === 'custom' && (!payload.startsAt || !payload.expiresAt)) {
      setShowFormErrors(true);
      setFormError('Start date must not be after the end date');
      return;
    }
    if (editingId) {
      updateMut.mutate({ id: editingId, payload });
    } else {
      createMut.mutate(payload);
    }
  };

  const rows = data?.data || [];
  const totalRows = data?.total ?? 0;

  useEffect(() => {
    if (!data || isLoading) return;
    const lastPage = Math.max(0, Math.ceil(data.total / pagination.pageSize) - 1);
    if (pagination.page > lastPage) {
      setPagination((p) => ({ ...p, page: lastPage }));
    }
  }, [data, isLoading, pagination.page, pagination.pageSize]);

  const columns: GridColDef<SalesDemoRow>[] = withSerialNo([
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1.4,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.75, lineHeight: 1.35, overflow: 'hidden', maxWidth: '100%' }}>
          <Tooltip title={row.companyName || ''} arrow placement="top-start" enterDelay={300}>
            <Typography
              variant="body2"
              fontWeight={700}
              noWrap
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setViewTarget(row);
              }}
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                border: 0,
                background: 'none',
                p: 0,
                m: 0,
                cursor: 'pointer',
                textAlign: 'left',
                color: 'primary.main',
                font: 'inherit',
                fontWeight: 700,
                '&:hover': { textDecoration: 'underline' },
              }}
              aria-label={`View full company name: ${row.companyName}`}
            >
              {row.companyName}
            </Typography>
          </Tooltip>
          <Tooltip title={row.slug ? `Slug: ${row.slug}` : ''} arrow placement="bottom-start" enterDelay={300}>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {row.slug}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'demoClientEmail',
      headerName: 'Email',
      flex: 1.1,
      minWidth: 180,
      renderCell: ({ row }) => {
        const email = (row.demoClientEmail || '').trim();
        if (!email) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Tooltip title={email} arrow placement="top" enterDelay={250}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'default',
              }}
            >
              {email}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'demoStartsAt',
      headerName: 'Start',
      width: 120,
      renderCell: ({ row }) => {
        const bad = Boolean(
          row.demoStartsAt
          && row.demoExpiresAt
          && isStartAfterEnd(row.demoStartsAt, row.demoExpiresAt),
        );
        return (
          <Typography variant="body2" noWrap color={bad ? 'error' : 'inherit'} title={formatDemoDate(row.demoStartsAt)}>
            {formatDemoDate(row.demoStartsAt)}
          </Typography>
        );
      },
    },
    {
      field: 'demoExpiresAt',
      headerName: 'End',
      width: 140,
      renderCell: ({ row }) => {
        const bad = Boolean(
          row.demoStartsAt
          && row.demoExpiresAt
          && isStartAfterEnd(row.demoStartsAt, row.demoExpiresAt),
        );
        const expired = Boolean(
          row.demoExpiresAt && new Date(row.demoExpiresAt).getTime() <= Date.now(),
        );
        return (
          <Typography
            variant="body2"
            noWrap
            color={bad || expired ? 'error' : 'inherit'}
            title={formatDemoDate(row.demoExpiresAt)}
          >
            {formatDemoDate(row.demoExpiresAt)}
            {expired ? ' · expired' : ''}
          </Typography>
        );
      },
    },
    {
      field: 'remainingDays',
      headerName: 'Remaining Days',
      // Fits uppercase "REMAINING DAYS" + sort icon under themed header styles
      width: 180,
      minWidth: 180,
      valueGetter: (_v, row) => remainingDaysForRow(row as SalesDemoRow),
      renderCell: ({ row }) => {
        const expired = resolveLifecycleStatus(row) === 'expired';
        const days = remainingDaysForRow(row);
        return (
          <Chip
            size="small"
            label={expired ? 'Expired · 0' : String(days)}
            color={expired ? 'warning' : days <= 3 ? 'warning' : 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'lifecycleStatus',
      headerName: 'Status',
      width: 200,
      sortable: false,
      valueGetter: (_v, row) => resolveLifecycleStatus(row as SalesDemoRow),
      renderCell: ({ row }) => {
        const life = resolveLifecycleStatus(row);
        if (life === 'scheduled') {
          return <Chip size="small" label="Scheduled" color="info" />;
        }
        const value = life === 'expired' ? 'expired' : 'active';
        const busy = statusToggleId === row._id && (revokeMut.isPending || extendMut.isPending);
        return (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value}
            disabled={busy}
            onChange={(_, next: 'active' | 'expired' | null) => {
              if (!next) return;
              toggleDemoStatus(row, next);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1,
                py: 0.25,
                fontSize: 12,
                textTransform: 'none',
                lineHeight: 1.4,
              },
            }}
          >
            <ToggleButton value="active" color="success" aria-label="Set active">
              Active
            </ToggleButton>
            <ToggleButton value="expired" color="warning" aria-label="Set expired">
              Expired
            </ToggleButton>
          </ToggleButtonGroup>
        );
      },
    },
    {
      field: 'seedStatus',
      headerName: 'Seed',
      width: 140,
      sortable: false,
      renderCell: ({ row }) => seedProgressCell(row),
    },
    {
      field: 'modules',
      headerName: 'Services',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <ServicesCell keys={enabledServiceKeys(row)} nameByCode={serviceNameByCode} visible={4} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Actions">
          <IconButton
            size="small"
            onClick={(e) => {
              setActionAnchor(e.currentTarget);
              setActionRow(row);
            }}
            aria-label="Demo actions"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]);

  return (
    <PageShell sx={{ overflow: 'visible' }}>
      <PageHeader
        eyebrow="Client Management"
        title="Demo Management"
        subtitle="Create time-boxed demo clients. One demo key = one company. Data is kept after expiry."
        actions={(
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => exportDemosCsv(rows)}
              disabled={!rows.length}
              sx={{ textTransform: 'none' }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateDialog}
              sx={{ textTransform: 'none' }}
            >
              Create Demo Client
            </Button>
          </Stack>
        )}
      />

      {actionFlash && (
        <Alert
          severity={actionFlash.severity}
          sx={{
            mb: 1.5,
            alignItems: 'center',
            color: 'text.primary',
            bgcolor: actionFlash.severity === 'success'
              ? 'success.light'
              : actionFlash.severity === 'warning'
                ? 'warning.light'
                : undefined,
            '& .MuiAlert-message': {
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.9375rem',
              lineHeight: 1.45,
            },
            '& .MuiAlert-icon': { color: 'inherit', opacity: 1 },
          }}
          onClose={() => setActionFlash(null)}
        >
          {actionFlash.message}
        </Alert>
      )}

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={(
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => void refetch()}>
              Retry
            </Button>
          )}
        >
          {extractError(error)} — Live list empty if API fails or live Mongo has no demos (local DB ≠ live DB).
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mb: 2, overflow: 'visible' }}>
        <TextField
          size="small"
          placeholder="Search company, slug, email, owner…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })); }}
          sx={{ maxWidth: 420, width: '100%' }}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <Grid container spacing={1.5} sx={{ overflow: 'visible', maxWidth: 720 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                size="small"
                fullWidth
                label="Status"
                value={statusFilter === 'locked' ? 'expired' : statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as DemoListStatus);
                  setPagination((p) => ({ ...p, page: 0 }));
                }}
                helperText="Combined with date range (AND)"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                size="small"
                fullWidth
                label="Date field"
                value={dateField}
                onChange={(e) => {
                  setDateField(e.target.value as DateField);
                  setPagination((p) => ({ ...p, page: 0 }));
                  if (datePreset === 'all' || (!customFrom && !customTo)) {
                    applyDatePreset('7');
                  }
                }}
                helperText="Which date From/To use"
              >
                <MenuItem value="demoStartsAt">Start date</MenuItem>
                <MenuItem value="demoExpiresAt">End date</MenuItem>
                <MenuItem value="createdAt">Created at</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={`From (${dateFieldLabel})`}
                format="DD/MM/YYYY"
                value={customFrom ? dayjs(customFrom) : null}
                maxDate={customTo ? dayjs(customTo) : undefined}
                onChange={setFilterFrom}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: dateRangeInvalid,
                    helperText: dateRangeInvalid ? 'From must be on/before To' : 'Click to open calendar',
                  },
                  field: { clearable: true },
                  popper: { sx: { zIndex: (t) => t.zIndex.modal + 2 } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={`To (${dateFieldLabel})`}
                format="DD/MM/YYYY"
                value={customTo ? dayjs(customTo) : null}
                minDate={customFrom ? dayjs(customFrom) : undefined}
                onChange={setFilterTo}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: dateRangeInvalid,
                    helperText: 'Click to open calendar',
                  },
                  field: { clearable: true },
                  popper: { sx: { zIndex: (t) => t.zIndex.modal + 2 } },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={datePreset}
            onChange={(_, v: DatePreset | null) => {
              if (!v) return;
              applyDatePreset(v);
            }}
          >
            <ToggleButton value="all">All time</ToggleButton>
            <ToggleButton value="7">Last 7 days</ToggleButton>
            <ToggleButton value="30">Last 30 days</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>
          {(customFrom || customTo || datePreset !== 'all') && (
            <Button
              size="small"
              onClick={() => applyDatePreset('all')}
              sx={{ textTransform: 'none' }}
            >
              Clear dates
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip size="small" label={`Status: ${statusFilterLabel}`} variant="outlined" />
          {(listRange.from || listRange.to) && !dateRangeInvalid ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${dateFieldLabel}: ${formatDemoDate(listRange.from)}${
                listRange.to && listRange.to !== listRange.from
                  ? ` → ${formatDemoDate(listRange.to)}`
                  : listRange.to
                    ? ` → ${formatDemoDate(listRange.to)}`
                    : ''
              }`}
            />
          ) : (
            <Chip size="small" label="Date: All time" variant="outlined" />
          )}
          <Typography variant="caption" color="text.secondary">
            Results must match both filters
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r._id}
          loading={isLoading}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25]}
          rowCount={totalRows}
          paginationMode="server"
          disableRowSelectionOnClick
          getRowHeight={() => 64}
          sx={{
            '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, whiteSpace: 'nowrap' },
          }}
        />
      </Box>

      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor) && Boolean(actionRow)}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (actionRow) setViewTarget(actionRow);
            closeActionMenu();
          }}
        >
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionRow) openEditDialog(actionRow);
            closeActionMenu();
          }}
        >
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            setExtendTarget(actionRow);
            setExtendPreset('7');
            setExtendDays(7);
            setExtendExpiresAt(
              actionRow.demoExpiresAt
                ? addCalendarDays(toDateOnlyString(actionRow.demoExpiresAt), 7)
                : addCalendarDays(todayDateOnly(), 7),
            );
            setExtendError('');
            closeActionMenu();
          }}
        >
          <ListItemIcon><Schedule fontSize="small" /></ListItemIcon>
          <ListItemText>Extend</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            const url = demoLoginUrl(actionRow.slug);
            copyText(url);
            setActionFlash({ severity: 'success', message: `Login URL copied: ${url}` });
            closeActionMenu();
          }}
        >
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy Login URL</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            resendMut.mutate(actionRow._id);
            closeActionMenu();
          }}
          disabled={resendMut.isPending}
        >
          <ListItemIcon><Email fontSize="small" /></ListItemIcon>
          <ListItemText>Resend Login Email</ListItemText>
        </MenuItem>
        <Divider />
        {actionRow && resolveLifecycleStatus(actionRow) !== 'expired' ? (
          <MenuItem
            onClick={() => {
              if (!actionRow) return;
              setStatusToggleId(actionRow._id);
              revokeMut.mutate(actionRow._id);
              closeActionMenu();
            }}
            disabled={revokeMut.isPending}
          >
            <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Disable Demo"
              secondary="Revoke access — demo becomes Expired"
            />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              if (!actionRow) return;
              setStatusToggleId(actionRow._id);
              extendMut.mutate({ id: actionRow._id, days: 7 });
              closeActionMenu();
            }}
            disabled={extendMut.isPending || !actionRow}
          >
            <ListItemIcon><PlayArrow fontSize="small" color="success" /></ListItemIcon>
            <ListItemText
              primary="Enable Demo"
              secondary="Reactivate and extend +7 days"
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            setDeleteTarget(actionRow);
            closeActionMenu();
          }}
          disabled={deleteMut.isPending}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete Demo Client</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={createOpen}
        onClose={() => { if (!formSaving) closeFormDialog(); }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{isEditMode ? 'Edit Demo Client' : 'Create Demo Client'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 0.5 }}>
                {formError}
              </Alert>
            )}
            {(duplicateFieldErrors.companyName
              || duplicateFieldErrors.clientEmail
              || duplicateFieldErrors.phone) && !formError && (
              <Alert severity="error">
                {[
                  duplicateFieldErrors.companyName,
                  duplicateFieldErrors.clientEmail,
                  duplicateFieldErrors.phone,
                ].filter(Boolean).join('. ')}
              </Alert>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  required
                  fullWidth
                  value={form.companyName}
                  onChange={(e) => patchForm({ companyName: e.target.value })}
                  error={Boolean(duplicateFieldErrors.companyName) || (showFormErrors && Boolean(companyError))}
                  helperText={
                    duplicateFieldErrors.companyName
                    || (showFormErrors ? companyError : undefined)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PersonNameField
                  label="Contact Person"
                  fullWidth
                  value={form.contactPerson}
                  onChange={(e) => patchForm({ contactPerson: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  autoComplete="email"
                  inputProps={{
                    inputMode: 'email',
                    'aria-required': true,
                  }}
                  helperText={emailHelperText}
                  error={showEmailFieldError}
                  value={form.clientEmail}
                  onChange={(e) => {
                    setEmailTouched(true);
                    patchForm({ clientEmail: e.target.value });
                  }}
                  onBlur={() => setEmailTouched(true)}
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneWithDialCodeField
                  label="Phone"
                  layout="stacked"
                  preferredCountry={form.country || 'IN'}
                  phone={form.phone}
                  countryCode={form.phoneCountryCode || '+91'}
                  error={showPhoneFieldError}
                  helperText={showPhoneFieldError ? phoneError : phoneLengthHint}
                  onChange={({ phone, countryCode }) => {
                    patchForm({ phone, phoneCountryCode: countryCode || '+91' });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GeoMasterSelect
                  label="Country"
                  masterType="countries"
                  value={form.country}
                  required
                  onChange={(code, opt) => {
                    const d = defaultsForCountrySelection(code, opt);
                    const preferred = dialOptions.find((o) => o.code === code.toUpperCase());
                    patchForm({
                      country: code,
                      timezone: d.timezone,
                      currency: d.currency,
                      ...(preferred && !localPhone
                        ? { phoneCountryCode: preferred.dialCode }
                        : {}),
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  value={form.currency}
                  helperText="Options depend on country"
                  onChange={(e) => patchForm({ currency: e.target.value })}
                >
                  {currencyOptionsForCountry(form.country, form.currency).map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Timezone"
                  value={form.timezone}
                  helperText="Options depend on country"
                  onChange={(e) => patchForm({ timezone: e.target.value })}
                >
                  {timezoneOptionsForCountry(form.country, form.timezone).map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Duration"
                  value={form.durationPreset}
                  onChange={(e) => applyDurationPreset(e.target.value as DurationPreset)}
                  helperText="Presets set End = Start + duration (DD/MM/YYYY). Custom: End must be after Start."
                >
                  {DEMO_DURATION_PRESETS.map((d) => (
                    <MenuItem key={d} value={String(d)}>{d} day{d === 1 ? '' : 's'}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom (start–end)</MenuItem>
                </TextField>
              </Grid>
              {form.durationPreset === 'custom' ? (
                <>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start date"
                      InputLabelProps={{ shrink: true }}
                      value={form.startsAt}
                      inputProps={{ max: form.expiresAt || undefined }}
                      onChange={(e) => {
                        const startsAt = e.target.value;
                        const next: Partial<CreateFormState> = { startsAt };
                        if (startsAt && form.expiresAt && !isStartAfterEnd(startsAt, form.expiresAt)) {
                          next.days = daysBetween(startsAt, form.expiresAt) || form.days;
                        }
                        patchForm(next);
                        setFormError('');
                      }}
                      error={Boolean(windowErr)}
                      helperText={windowErr || undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End date"
                      InputLabelProps={{ shrink: true }}
                      value={form.expiresAt}
                      inputProps={{ min: form.startsAt || undefined }}
                      onChange={(e) => {
                        const expiresAt = e.target.value;
                        const next: Partial<CreateFormState> = { expiresAt };
                        if (form.startsAt && expiresAt && !isStartAfterEnd(form.startsAt, expiresAt)) {
                          next.days = daysBetween(form.startsAt, expiresAt) || form.days;
                        }
                        patchForm(next);
                        setFormError('');
                      }}
                      helperText={
                        windowErr
                          || (customWindowDays > 0
                            ? `${customWindowDays} day${customWindowDays === 1 ? '' : 's'} (max 90)`
                            : 'End date must be after start date')
                      }
                      error={Boolean(windowErr)}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start date"
                    InputLabelProps={{ shrink: true }}
                    value={form.startsAt}
                    onChange={(e) => {
                      const startsAt = e.target.value;
                      patchForm({
                        startsAt,
                        expiresAt: startsAt
                          ? addCalendarDays(startsAt, form.days)
                          : form.expiresAt,
                      });
                      setFormError('');
                    }}
                    helperText={
                      form.expiresAt
                        ? `Ends ${formatDemoDate(form.expiresAt)} (${form.days} day${form.days === 1 ? '' : 's'}) · leave blank to start now`
                        : 'Leave blank to start immediately'
                    }
                  />
                </Grid>
              )}
            </Grid>

            <Typography variant="subtitle2" fontWeight={700}>Enabled Services</Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
              {(serviceModules.length ? serviceModules.map((s) => s.code) : DEFAULT_MODULES).map((code) => (
                <FormControlLabel
                  key={code}
                  control={(
                    <Checkbox
                      size="small"
                      checked={form.enabledModules.includes(code)}
                      onChange={() => toggleModule(code)}
                    />
                  )}
                  label={code}
                />
              ))}
            </Stack>

            <Accordion
              disableGutters
              elevation={0}
              expanded={advancedOpen}
              onChange={(_, exp) => setAdvancedOpen(exp)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2" fontWeight={700}>Branding &amp; advanced</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Logo URL"
                      value={form.logo}
                      onChange={(e) => patchForm({ logo: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Theme / primary color"
                      type="color"
                      value={form.primaryColor}
                      onChange={(e) => patchForm({ primaryColor: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {isEditMode
                        ? <>Slug <code>{editingSlug || 'demo-…'}</code> is fixed. Demo key updates when you use Resend Login Email.</>
                        : <>Domain / slug is auto-generated as <code>demo-{'{company}'}</code> after create (API does not accept a custom slug).</>}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Subscription plan"
                      value={form.subscriptionPlan}
                      onChange={(e) => patchForm({ subscriptionPlan: e.target.value })}
                    >
                      {planOptions.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Sales owner"
                      placeholder="Internal AE / owner"
                      value={form.salesOwner}
                      onChange={(e) => patchForm({ salesOwner: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Internal notes"
                      multiline
                      minRows={2}
                      value={form.notes}
                      onChange={(e) => patchForm({ notes: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog} disabled={formSaving}>Cancel</Button>
          <Button
            type="button"
            variant="contained"
            disabled={createDisabled}
            onClick={submitForm}
            aria-disabled={createDisabled}
          >
            {formSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Demo client details</DialogTitle>
        <DialogContent>
          {viewTarget && (
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={700}>{viewTarget.companyName}</Typography>
                {statusChip(viewTarget)}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Slug / domain: <code>{viewTarget.slug}</code>
              </Typography>
              <Typography variant="body2">Admin email: {viewTarget.demoClientEmail || '—'}</Typography>
              <Typography variant="body2">Vendor email: {viewTarget.demoVendorEmail || '—'}</Typography>
              <Typography variant="body2">
                Window:{' '}
                {formatDemoDate(viewTarget.demoStartsAt)}
                {' → '}
                {formatDemoDate(viewTarget.demoExpiresAt)}
                {viewTarget.demoStartsAt && viewTarget.demoExpiresAt
                  ? ` (${daysBetween(viewTarget.demoStartsAt, viewTarget.demoExpiresAt)} day window)`
                  : ''}
              </Typography>
              <Typography variant="body2">
                Remaining days:{' '}
                {resolveLifecycleStatus(viewTarget) === 'expired'
                  ? '0 (expired)'
                  : remainingDaysForRow(viewTarget)}
              </Typography>
              <Typography variant="body2">
                Key prefix: {viewTarget.demoKeyPrefix || '—'}
              </Typography>
              <Typography variant="body2">
                Locale: {[viewTarget.country, viewTarget.currency, viewTarget.timezone].filter(Boolean).join(' · ') || '—'}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
                {enabledServiceKeys(viewTarget).map((k) => (
                  <Chip key={k} size="small" label={k} />
                ))}
              </Stack>
              {viewTarget.demoNotes && (
                <Typography variant="body2" color="text.secondary">{viewTarget.demoNotes}</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopy />}
            onClick={() => {
              if (!viewTarget) return;
              copyText(demoLoginUrl(viewTarget.slug));
              setActionFlash({ severity: 'success', message: 'Login URL copied.' });
            }}
          >
            Copy Login URL
          </Button>
          <Button variant="contained" onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!extendTarget} onClose={() => !extendMut.isPending && setExtendTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Extend demo</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {extendError && <Alert severity="error">{extendError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {extendTarget?.companyName}
              {extendTarget?.demoExpiresAt
                ? ` · current expiry ${formatDemoDate(extendTarget.demoExpiresAt)}`
                : ''}
            </Typography>
            <TextField
              select
              fullWidth
              label="Extend by"
              value={extendPreset}
              onChange={(e) => applyExtendPreset(e.target.value as DurationPreset)}
            >
              {DEMO_DURATION_PRESETS.map((d) => (
                <MenuItem key={d} value={String(d)}>{d} day{d === 1 ? '' : 's'}</MenuItem>
              ))}
              <MenuItem value="custom">Custom end date</MenuItem>
            </TextField>
            {extendPreset === 'custom' ? (
              <TextField
                label="New end date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={extendExpiresAt}
                inputProps={{
                  min: extendTarget?.demoStartsAt
                    ? toDateOnlyString(extendTarget.demoStartsAt)
                    : undefined,
                }}
                error={Boolean(
                  extendExpiresAt
                  && extendTarget?.demoStartsAt
                  && isStartAfterEnd(
                    toDateOnlyString(extendTarget.demoStartsAt),
                    extendExpiresAt,
                  ),
                )}
                helperText={
                  extendExpiresAt
                  && extendTarget?.demoStartsAt
                  && isStartAfterEnd(
                    toDateOnlyString(extendTarget.demoStartsAt),
                    extendExpiresAt,
                  )
                    ? 'End date must be after the demo start date'
                    : undefined
                }
                onChange={(e) => setExtendExpiresAt(e.target.value)}
              />
            ) : (
              <TextField
                label="Days to add"
                type="number"
                fullWidth
                inputProps={{ min: 1, max: 90 }}
                value={extendDays}
                onChange={(e) => {
                  const days = Math.max(1, Math.min(90, Number(e.target.value) || 7));
                  setExtendDays(days);
                  const match = DEMO_DURATION_PRESETS.find((p) => p === days);
                  setExtendPreset(match ? String(match) as DurationPreset : 'custom');
                }}
                helperText="Or pick a preset above (1 / 3 / 7 / 15 / 30)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendTarget(null)} disabled={extendMut.isPending}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              extendMut.isPending
              || (extendPreset === 'custom' && !extendExpiresAt)
              || (
                extendPreset === 'custom'
                && !!extendExpiresAt
                && !!extendTarget?.demoStartsAt
                && isStartAfterEnd(
                  toDateOnlyString(extendTarget.demoStartsAt),
                  extendExpiresAt,
                )
              )
            }
            onClick={() => {
              if (!extendTarget) return;
              if (
                extendPreset === 'custom'
                && extendExpiresAt
                && extendTarget.demoStartsAt
                && isStartAfterEnd(
                  toDateOnlyString(extendTarget.demoStartsAt),
                  extendExpiresAt,
                )
              ) {
                setExtendError('Start date must not be after the end date');
                return;
              }
              extendMut.mutate(
                extendPreset === 'custom'
                  ? { id: extendTarget._id, expiresAt: extendExpiresAt }
                  : { id: extendTarget._id, days: extendDays },
              );
            }}
          >
            {extendMut.isPending ? 'Saving…' : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteMut.isPending && setDeleteTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 800 }}>
          Delete demo client?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.75} sx={{ mt: 0.5 }}>
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                color: 'text.primary',
                borderColor: 'error.main',
                bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(211,47,47,0.16)',
                '& .MuiAlert-message': {
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  lineHeight: 1.5,
                },
                '& .MuiAlert-icon': { color: 'error.main', opacity: 1 },
                '& strong': { color: 'text.primary', fontWeight: 800 },
              }}
            >
              This permanently deletes <strong>{deleteTarget?.companyName}</strong> and all seeded
              demo data (users, masters, zones, catalogs, orders). The demo key stops working.
              This cannot be undone.
            </Alert>
            <Typography
              variant="body1"
              sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.5 }}
            >
              After you confirm, the status will show that this demo client data has been deleted.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMut.isPending}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMut.isPending || !deleteTarget}
            onClick={() => {
              if (!deleteTarget) return;
              deleteMut.mutate(deleteTarget._id);
            }}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>Demo ready — share invite details</DialogTitle>
        <DialogContent>
          {created && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Alert severity={created.emailDelivered ? 'success' : 'warning'}>
                {created.emailDelivered
                  ? `Invite emailed to ${created.adminEmail || created.tenant.demoClientEmail} with admin URL, app links, and passwords.`
                  : (
                    <>
                      Invite email was <strong>not delivered</strong>
                      {created.emailReason ? `: ${created.emailReason}` : ' (SMTP not configured on the server)'}.
                      {' '}Copy the details below or use “Open in email app” to share Admin URL, mobile app URLs, and passwords.
                    </>
                  )}
              </Alert>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={700}>{created.tenant.companyName}</Typography>
                {statusChip(created.tenant)}
              </Stack>
              <Typography variant="subtitle2" fontWeight={700}>Credentials</Typography>
              <CredentialField label="Demo Key" value={created.demoKey} />
              <CredentialField
                label="Admin Email"
                value={created.adminEmail || created.tenant.demoClientEmail || ''}
              />
              <CredentialField label="Admin Password" value={created.adminPassword} />
              <CredentialField label="Vendor Email" value={created.vendorEmail || ''} />
              <CredentialField label="Vendor Password" value={created.vendorPassword || ''} />
              <Typography variant="subtitle2" fontWeight={700}>URLs</Typography>
              <CredentialField label="Admin Login URL" value={created.demoUrl || demoLoginUrl(created.tenant.slug)} />
              <CredentialField label="Vendor Login URL" value={created.vendorUrl || demoLoginUrl(created.tenant.slug, 'vendor')} />
              <Typography variant="subtitle2" fontWeight={700}>Mobile apps (Play Store)</Typography>
              <CredentialField label="User App" value={(created.appLinks || DEFAULT_APP_LINKS).user} />
              <CredentialField label="Delivery Partner App" value={(created.appLinks || DEFAULT_APP_LINKS).partner} />
              <CredentialField label="On-demand Provider App" value={(created.appLinks || DEFAULT_APP_LINKS).provider} />
              <CredentialField label="Vendor App" value={(created.appLinks || DEFAULT_APP_LINKS).vendor} />
              <Typography variant="subtitle2" fontWeight={700}>Mobile apps (App Store)</Typography>
              <CredentialField label="User App" value={(created.appleLinks || DEFAULT_APPLE_LINKS).user} />
              <CredentialField label="Delivery Partner App" value={(created.appleLinks || DEFAULT_APPLE_LINKS).partner} />
              <CredentialField label="On-demand Provider App" value={(created.appleLinks || DEFAULT_APPLE_LINKS).provider} />
              <CredentialField label="Vendor App" value={(created.appleLinks || DEFAULT_APPLE_LINKS).vendor} />
              <Typography variant="body2" color="text.secondary">
                {created.startsAt ? `Starts ${formatDemoDate(String(created.startsAt))} · ` : ''}
                Expires {formatDemoDate(String(created.expiresAt))}
                {created.startsAt && created.expiresAt
                  ? ` (${daysBetween(String(created.startsAt), String(created.expiresAt))} days)`
                  : ''}
                {' · '}
                {created.days} day{created.days === 1 ? '' : 's'}
                {' · '}
                Slug <code>{created.tenant.slug}</code>
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {created && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => {
                  copyText(buildDemoInviteShareText(created));
                  setActionFlash({ severity: 'success', message: 'Full invite text copied — paste into email or chat.' });
                }}
              >
                Copy full invite
              </Button>
              <Button
                variant="outlined"
                startIcon={<Email />}
                component="a"
                href={`mailto:${encodeURIComponent(created.adminEmail || created.tenant.demoClientEmail || '')}?subject=${encodeURIComponent(`Welcome to Xmoveon — ${created.tenant.companyName} demo`)}&body=${encodeURIComponent(buildDemoInviteShareText(created))}`}
              >
                Open in email app
              </Button>
              <Button
                variant="outlined"
                disabled={resendMut.isPending}
                onClick={() => {
                  resendMut.mutate(created.tenant._id);
                }}
              >
                {resendMut.isPending ? 'Resending…' : 'Resend invite email'}
              </Button>
            </>
          )}
          <Button variant="contained" onClick={() => setCreated(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!inviteResult} onClose={() => setInviteResult(null)} fullWidth maxWidth="sm">
        <DialogTitle>Login details resent</DialogTitle>
        <DialogContent>
          {inviteResult && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Alert severity={inviteResult.emailDelivered ? 'success' : 'warning'}>
                {inviteResult.emailDelivered
                  ? `Login details emailed to ${inviteResult.adminEmail} (admin URL, app links, passwords).`
                  : (
                    <>
                      Email was <strong>not delivered</strong>
                      {inviteResult.emailReason ? `: ${inviteResult.emailReason}` : ' (SMTP not configured on the server)'}.
                      {' '}Use Copy full invite or Open in email app to share the details below.
                    </>
                  )}
              </Alert>
              {inviteResult.companyName && (
                <Typography variant="body2" fontWeight={700}>{inviteResult.companyName}</Typography>
              )}
              <Typography variant="subtitle2" fontWeight={700}>Credentials</Typography>
              <CredentialField label="Demo Key" value={inviteResult.demoKey} />
              <CredentialField label="Admin Email" value={inviteResult.adminEmail || ''} />
              <CredentialField label="Admin Password" value={inviteResult.adminPassword} />
              <CredentialField label="Vendor Email" value={inviteResult.vendorEmail || ''} />
              <CredentialField label="Vendor Password" value={inviteResult.vendorPassword || ''} />
              <Typography variant="subtitle2" fontWeight={700}>URLs</Typography>
              <CredentialField label="Admin Login URL" value={inviteResult.demoUrl || demoLoginUrl(inviteResult.slug)} />
              <CredentialField label="Vendor Login URL" value={inviteResult.vendorUrl || demoLoginUrl(inviteResult.slug, 'vendor')} />
              <Typography variant="subtitle2" fontWeight={700}>Mobile apps (Play Store)</Typography>
              <CredentialField label="User App" value={(inviteResult.appLinks || DEFAULT_APP_LINKS).user} />
              <CredentialField label="Delivery Partner App" value={(inviteResult.appLinks || DEFAULT_APP_LINKS).partner} />
              <CredentialField label="On-demand Provider App" value={(inviteResult.appLinks || DEFAULT_APP_LINKS).provider} />
              <CredentialField label="Vendor App" value={(inviteResult.appLinks || DEFAULT_APP_LINKS).vendor} />
              <Typography variant="subtitle2" fontWeight={700}>Mobile apps (App Store)</Typography>
              <CredentialField label="User App" value={(inviteResult.appleLinks || DEFAULT_APPLE_LINKS).user} />
              <CredentialField label="Delivery Partner App" value={(inviteResult.appleLinks || DEFAULT_APPLE_LINKS).partner} />
              <CredentialField label="On-demand Provider App" value={(inviteResult.appleLinks || DEFAULT_APPLE_LINKS).provider} />
              <CredentialField label="Vendor App" value={(inviteResult.appleLinks || DEFAULT_APPLE_LINKS).vendor} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {inviteResult && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => {
                  copyText(buildResendInviteShareText(inviteResult));
                  setActionFlash({ severity: 'success', message: 'Full invite text copied — paste into email or chat.' });
                }}
              >
                Copy full invite
              </Button>
              <Button
                variant="outlined"
                startIcon={<Email />}
                component="a"
                href={`mailto:${encodeURIComponent(inviteResult.adminEmail || '')}?subject=${encodeURIComponent(`Xmoveon demo login — ${inviteResult.companyName || 'Demo'}`)}&body=${encodeURIComponent(buildResendInviteShareText(inviteResult))}`}
              >
                Open in email app
              </Button>
            </>
          )}
          <Button variant="contained" onClick={() => setInviteResult(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
